import { lazy, Suspense, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { GraduationCap, ArrowRight, ShieldCheck, Lightning, Translate } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { RoboSparkMark } from "../components/Welcome";
import { useT } from "../lib/i18n";
import { errMsg } from "../lib/api";

const FEATURES = [
  [ShieldCheck, "Isolated data for every school", "Each school's records stay private"],
  [Lightning, "Live attendance, fees & results", "Updates reach parents instantly"],
  [Translate, "English + हिंदी", "Switch the whole app in one tap"],
];
const Scene3D = lazy(() => import("../components/Scene3D"));
const hasWebGL = (() => {
  try {
    const c = document.createElement("canvas");
    return !!(window.WebGLRenderingContext && (c.getContext("webgl2") || c.getContext("webgl")));
  } catch {
    return false;
  }
})();

function useTilt() {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 80, damping: 18 });
  const sy = useSpring(my, { stiffness: 80, damping: 18 });
  const onPointerMove = (e) => {
    mx.set(e.clientX / window.innerWidth - 0.5);
    my.set(e.clientY / window.innerHeight - 0.5);
  };
  return { sx, sy, onPointerMove };
}

function FloatCard({ f: [I, title, sub], i, sx, sy }) {
  const rY = useTransform(sx, [-0.5, 0.5], [-14 - i * 4, 14 + i * 4]);
  const rX = useTransform(sy, [-0.5, 0.5], [10, -10]);
  return (
    <motion.div initial={{ opacity: 0, y: 30, scale: 0.92 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ delay: 0.35 + i * 0.12, duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
      style={{ rotateY: rY, rotateX: rX, transformStyle: "preserve-3d", marginLeft: `${i * 2.2}rem` }} className="w-fit" data-testid={`login-float-card-${i}`}>
      <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 5 + i, repeat: Infinity, ease: "easeInOut", delay: i * 0.6 }}
        className="glass-strong flex items-center gap-4 rounded-2xl px-5 py-4 shadow-[0_25px_50px_-20px_rgba(0,0,0,0.9),0_0_30px_-10px_rgba(59,130,246,0.5)]">
        <span className="grid h-11 w-11 place-items-center rounded-xl bg-gradient-to-br from-blue-500/30 to-orange-500/20 border border-white/10" style={{ transform: "translateZ(30px)" }}>
          <I size={20} weight="duotone" className="text-blue-300" />
        </span>
        <div style={{ transform: "translateZ(20px)" }}>
          <div className="text-sm font-semibold text-white">{title}</div>
          <div className="text-xs text-zinc-400">{sub}</div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const { t, lang, setLang } = useT();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { sx, sy, onPointerMove } = useTilt();
  const formRY = useTransform(sx, [-0.5, 0.5], [-7, 7]);
  const formRX = useTransform(sy, [-0.5, 0.5], [6, -6]);
  const mobile = window.innerWidth < 768;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const u = await login(email, password);
      sessionStorage.setItem("schola_just_logged_in", "1");
      nav(u.role === "superadmin" ? "/platform" : "/dashboard");
    } catch (er) {
      setErr(errMsg(er));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#05050a] text-white" onPointerMove={onPointerMove}>
      <div className="absolute inset-0 bg-cover bg-center opacity-60" style={{ backgroundImage: "url(/login-bg.jpg)" }} />
      <div className="absolute inset-0 bg-gradient-to-r from-[#05050a] via-[#05050a]/70 to-[#05050a]/30" />
      <div className="absolute inset-0 grid-lines opacity-60" />
      {hasWebGL && <div className="absolute inset-0"><Suspense fallback={null}><Scene3D mobile={mobile} /></Suspense></div>}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_30%,#05050a_95%)]" />
      <div className="grain" />
      <div className="pointer-events-none relative z-10 mx-auto grid min-h-screen max-w-[1320px] grid-cols-1 items-center gap-12 px-6 py-12 lg:grid-cols-[1.2fr_1fr] lg:px-12 [&>*]:pointer-events-auto">
        <motion.div initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}>
          <div className="flex items-center gap-3">
            <span className="grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-blue-500 to-orange-500 shadow-[0_10px_40px_-8px_rgba(59,130,246,0.8)]">
              <GraduationCap size={26} weight="fill" />
            </span>
            <span className="font-display text-2xl font-semibold tracking-tight">Schola<span className="text-orange-400">-AI</span></span>
          </div>
          <h1 className="mt-14 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.02] tracking-tight">
            <span className="text-gradient">The operating system</span>
            <br />for modern schools.
          </h1>
          <p className="mt-6 max-w-lg text-base text-zinc-300 leading-relaxed">
            Admissions, attendance, fees, exams and parent communication — unified in one secure, beautifully crafted workspace.
          </p>
          <div className="mt-10 hidden flex-col gap-4 sm:flex" style={{ perspective: 900 }}>
            {FEATURES.map((f, i) => <FloatCard key={f[1]} f={f} i={i} sx={sx} sy={sy} />)}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15, ease: [0.2, 0.8, 0.2, 1] }}
          style={{ transformPerspective: 1000, rotateY: formRY, rotateX: formRX }} className="w-full max-w-md lg:justify-self-end">
          <div className="glass-strong relative overflow-hidden rounded-[2rem] p-8 sm:p-10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)]">
            <div className="absolute -top-24 -right-24 h-56 w-56 rounded-full bg-blue-500/25 blur-3xl" />
            <div className="relative">
              <div className="flex items-center justify-between">
                <span className="eyebrow">Secure sign in</span>
                <button type="button" onClick={() => setLang(lang === "en" ? "hi" : "en")} className="btn-ghost !py-1 !px-3 text-xs" data-testid="login-lang-toggle">
                  {lang === "en" ? "हिंदी" : "English"}
                </button>
              </div>
              <h2 className="mt-4 font-display text-3xl font-semibold tracking-tight">{t("Welcome back")}</h2>
              <p className="mt-2 text-sm text-zinc-400">Admins, teachers, parents and students use the same door.</p>
              <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
                <label className="block">
                  <span className="eyebrow mb-2 block">{t("Email")}</span>
                  <input className="field !py-3" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@school.edu" data-testid="login-email-input" />
                </label>
                <label className="block">
                  <span className="mb-2 flex items-center justify-between"><span className="eyebrow">{t("Password")}</span>
                    <Link to="/forgot-password" className="text-xs text-blue-300 hover:text-blue-200" data-testid="login-forgot-link">Forgot password?</Link></span>
                  <input className="field !py-3" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="login-password-input" />
                </label>
                {err && <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-2.5 text-sm text-rose-200" data-testid="login-error">{err}</div>}
                <button className="btn-primary w-full !py-3" disabled={busy} data-testid="login-submit-button">
                  {busy ? "Signing in…" : t("Sign in")} <ArrowRight size={16} weight="bold" />
                </button>
              </form>
              <p className="mt-6 text-xs text-zinc-500">Accounts are issued by your school. Contact the school office if you can't sign in.</p>
              <div className="mt-8 flex items-center justify-center gap-2.5 border-t border-white/5 pt-6" data-testid="login-robospark">
                <span className="grid h-8 w-8 place-items-center rounded-full bg-gradient-to-br from-blue-500 to-blue-800 shadow-[0_0_16px_rgba(59,130,246,0.55)]">
                  <RoboSparkMark className="h-5 w-5" />
                </span>
                <span className="text-xs tracking-wide text-zinc-400">by <span className="font-semibold text-zinc-200">RoboSpark PrP Pvt Ltd</span></span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
